/**
 * Rolling a bench's solution rows up to one line per question.
 *
 * Pure, and in its own file for the same reason `benchScorecard.ts` is on the
 * backend: these counts are what someone decides where to spend an afternoon
 * on, so they should be testable without standing up the component.
 *
 * What this deliberately does NOT aggregate is what the analyses concluded
 * about the students — correctness, score. That is a property of the solution
 * (a wrong answer *should* score 0), so averaging it describes who was benched
 * rather than the models under test. The one score-derived figure kept is
 * disagreement BETWEEN models on one fixed solution, which is a model signal
 * and the only triage cue available before anyone has judged anything.
 */

import type { MatrixRow, MatrixCell, VerdictValue } from '../../types/scenarioEvals';

export interface VerdictTally { n: number; good: number; acceptable: number; bad: number }

export interface QuestionRollup {
    questionId: string;
    text: string;
    subject?: string;
    questionType?: string;
    solutions: number;
    /** Distinct students — several solutions can come from one person. */
    users: number;
    cells: number;
    readingCells: number;
    assessmentCells: number;
    /** Distinct prompts involved, by id — typically the extractor and the
     *  analyser. Counted across every cell, not per step. */
    prompts: number;
    /** Distinct providers that have run against this question. Two is the
     *  minimum for any comparison to be possible at all. */
    models: number;
    labeled: number;
    human: VerdictTally;
    judge: VerdictTally;
    /** Solutions two assessment models scored more than `DISPUTE_MARGIN` apart. */
    disputed: number;
    /** Solutions with more than one assessment score — the comparable ones.
     *  `disputed` is meaningless without it: 0 disputed out of 0 comparable is
     *  not agreement, it is an absence of evidence. */
    comparable: number;
    /** Versions of the assessment prompt seen across these cells. More than
     *  one means the results differ by prompt as well as by model, so they are
     *  not a clean model comparison. Distinct from `prompts`, which counts
     *  which prompts ran rather than how much one of them moved. */
    promptVersions: number;
}

/** Points apart before two models are called to disagree. Under this, they are
 *  splitting hairs about partial credit; over it, they disagree about the work. */
export const DISPUTE_MARGIN = 3;

const emptyTally = (): VerdictTally => ({ n: 0, good: 0, acceptable: 0, bad: 0 });

const isAssessment = (c: MatrixCell): boolean => c.role === 'assessment' || c.role === 'combined';

export function rollupByQuestion(rows: MatrixRow[]): QuestionRollup[] {
    const byQuestion = new Map<string, QuestionRollup & {
        userIds: Set<string>; versions: Set<number>;
        promptIds: Set<string>; providers: Set<string>;
    }>();

    for (const row of rows) {
        const q = row.input.question;
        const questionId = q?.id ?? 'unknown';
        let acc = byQuestion.get(questionId);
        if (!acc) {
            acc = {
                questionId,
                text: q?.text ?? '',
                subject: q?.subject,
                questionType: q?.questionType,
                solutions: 0, users: 0, cells: 0, readingCells: 0, assessmentCells: 0,
                prompts: 0, models: 0, labeled: 0,
                human: emptyTally(), judge: emptyTally(),
                disputed: 0, comparable: 0, promptVersions: 0,
                userIds: new Set<string>(), versions: new Set<number>(),
                promptIds: new Set<string>(), providers: new Set<string>(),
            };
            byQuestion.set(questionId, acc);
        }

        acc.solutions++;
        if (row.userId) acc.userIds.add(row.userId);
        if (row.input.label) acc.labeled++;

        const cells = row.chains.flatMap(c => c.cells);
        const assessment = cells.filter(isAssessment);

        acc.cells += cells.length;
        acc.readingCells += cells.filter(c => c.role === 'reading').length;
        acc.assessmentCells += assessment.length;

        for (const c of assessment) {
            if (c.promptVersion !== undefined) acc.versions.add(c.promptVersion);
        }
        for (const c of cells) {
            if (c.promptId) acc.promptIds.add(c.promptId);
            if (c.provider) acc.providers.add(c.provider);
            if (c.humanVerdict) { acc.human.n++; acc.human[c.humanVerdict.verdict as VerdictValue]++; }
            if (c.judgeVerdict) { acc.judge.n++; acc.judge[c.judgeVerdict.verdict as VerdictValue]++; }
        }

        const scores = assessment.map(c => c.score).filter((s): s is number => s !== undefined);
        if (scores.length > 1) {
            acc.comparable++;
            if (Math.max(...scores) - Math.min(...scores) > DISPUTE_MARGIN) acc.disputed++;
        }
    }

    return [...byQuestion.values()]
        .map(({ userIds, versions, promptIds, providers, ...q }) => ({
            ...q,
            users: userIds.size,
            promptVersions: versions.size,
            prompts: promptIds.size,
            models: providers.size,
        }))
        // Questions with something to say come before ones merely present.
        .sort((a, b) => b.disputed - a.disputed || b.solutions - a.solutions);
}
